/** @jsxImportSource @emotion/react */
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Formik, FormikConfig, FormikProps } from 'formik';
import { modalButton, modalError, modalInputStyle } from 'modules/template-editor/ui/components/elements/modals/styles';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, normalizeTemplateName } from 'modules/manager/domain/template-name-conflict';
import React from 'react';
import UDFormInput from 'ud-form/components/input';
import { UDModalDirtyGuard } from 'ud-form/components/modal-dirty-guard';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import * as yup from 'yup';

export type TemplateGroupFormValues = {
  name: string;
};

type Props = FormikConfig<TemplateGroupFormValues> & {
  isLoading: boolean;
  errorMessage?: string | null;
  submitLabel: React.ReactNode;
  existingNames?: string[];
};

export const TEMPLATE_GROUP_FORM_INITIAL_VALUES: TemplateGroupFormValues = { name: '' };

function MediaManagerTemplateGroupForm(props: Props) {
  const { isLoading, errorMessage, submitLabel, existingNames = [], ...rest } = props;

  const validator = yup.object({
    name: yup
      .string()
      .trim()
      .required(() => t`Введите название`)
      .max(255, () => t`Слишком длинное название`)
      .test(
        'unique',
        () => t(TEMPLATE_NAME_CONFLICT_MESSAGE),
        (value) => {
          if (!value) return true;
          const normalized = normalizeTemplateName(value);
          return !existingNames.some((name) => normalizeTemplateName(name) === normalized);
        },
      ),
  });

  const getError = (formProps: FormikProps<TemplateGroupFormValues>): string | undefined =>
    (formProps.touched.name ? formProps.errors.name : undefined) ?? errorMessage ?? undefined;

  return (
    <Formik validateOnChange validationSchema={validator} {...rest}>
      {(formProps) => (
        <form onSubmit={formProps.handleSubmit} className="w-full">
          <UDModalDirtyGuard />
          <Modal.Body className="flex flex-col gap-3">
            <UDFormInput readOnly={isLoading} name="name" autoFocus css={modalInputStyle} placeholder={t`Название группы`} />
          </Modal.Body>
          <Modal.Footer justify="start">
            {/* Замок — от Formik: `onSubmit` модалки возвращает промис (IMGM-1561). */}
            <UDButton type="submit" variant="primary" className="flex-center" disabled={!formProps.isValid} css={modalButton}>
              {submitLabel}
            </UDButton>
            <span css={modalError}>{getError(formProps)}</span>
          </Modal.Footer>
        </form>
      )}
    </Formik>
  );
}

export default MediaManagerTemplateGroupForm;
